import type { PovliEvent } from '../types/analytics'

type MetaEvent = 'PageView' | 'StartReading' | 'ReachLock' | 'ClickUnlock' | 'ClickOpenApp' | 'ClickGooglePlay'
type EventProperties = Record<string, string | number | boolean | null>
type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void
  loaded?: boolean
  push?: Fbq
  queue?: unknown[][]
  version?: string
}

declare global {
  interface Window {
    fbq?: Fbq
    _fbq?: Fbq
  }
}

let enabled = false

export function metaEventName(event: PovliEvent): MetaEvent | undefined {
  if (event === 'LandingPageView') return 'PageView'
  if (event === 'StartReading' || event === 'ReachLock' || event === 'ClickUnlock' || event === 'ClickOpenApp' || event === 'ClickGooglePlay') return event
  return undefined
}

export function isMetaPixelPath(pathname: string): boolean {
  return /^\/r\/[^/]+\/?$/.test(pathname)
}

export function initMetaPixel(): boolean {
  if (!isMetaPixelPath(window.location.pathname)) return false
  const pixelId = import.meta.env.VITE_META_PIXEL_ID?.trim()
  if (!pixelId || pixelId === 'replace_me' || !/^\d{5,30}$/.test(pixelId)) return false

  if (!window.fbq) {
    const fbq = function (...args: unknown[]) {
      if (fbq.callMethod) fbq.callMethod(...args)
      else fbq.queue?.push(args)
    } as Fbq
    fbq.push = fbq
    fbq.loaded = true
    fbq.version = '2.0'
    fbq.queue = []
    window.fbq = fbq
    window._fbq = fbq

    const script = document.createElement('script')
    script.async = true
    script.src = 'https://connect.facebook.net/en_US/fbevents.js'
    document.head.appendChild(script)
  }

  window.fbq('init', pixelId)
  enabled = true
  return true
}

export function captureMetaEvent(event: PovliEvent, properties: EventProperties, eventId: string): void {
  const metaEvent = metaEventName(event)
  if (!enabled || !window.fbq || !metaEvent) return

  const customData = Object.fromEntries(
    Object.entries(properties).filter(([, value]) => value !== null),
  )
  customData.event_id = eventId

  if (metaEvent === 'PageView') {
    window.fbq('track', metaEvent, customData, { eventID: eventId })
  } else {
    window.fbq('trackCustom', metaEvent, customData, { eventID: eventId })
  }
}
